import { createClient, SupabaseClient, RealtimeChannel } from "@supabase/supabase-js";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "env";

/**
 * Supabase Realtime dùng chung cho phòng chat.
 *
 * Hướng tích hợp (xem RoomChat/index.tsx và RoomChat/ChatWindow.tsx):
 *   nhận event từ bảng channel_messages -> refetch API hiện có.
 * Nhờ vậy không cần đổi logic map/render dữ liệu, chỉ thay "chuông báo"
 * polling 3s bằng websocket realtime, kèm polling chậm làm dự phòng.
 *
 * Nếu chưa cấu hình SUPABASE_URL / SUPABASE_ANON_KEY thì isRealtimeEnabled()
 * trả về false và các component sẽ tự dùng lại polling như cũ.
 */

export const NOTIFICATIONS_TABLE = "notifications";

// Tên bảng + cột filter — CẦN khớp với schema thực tế của Supabase.
// Nếu cột không tồn tại, subscription sẽ báo CHANNEL_ERROR và không nhận event;
// khi đó phòng chat vẫn an toàn nhờ polling chậm dự phòng.
export const CHANNEL_MESSAGES_TABLE = "channel_messages";
export const COL_CONNECTION_ID = "channel_connection_id";
export const COL_CUSTOMER_ID = "customer_id";

export const isRealtimeEnabled = (): boolean => Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

let client: SupabaseClient | null = null;

// Một client dùng chung cho toàn app, tránh mở nhiều websocket.
const getClient = (): SupabaseClient | null => {
    if (!isRealtimeEnabled()) return null;
    if (!client) {
        client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
            auth: { persistSession: false },
        });
    }
    return client;
};

export interface SubscribeOptions {
    /** Tên kênh duy nhất để tránh trùng subscription giữa các component. */
    channelName: string;
    /** Filter dạng postgres_changes, vd: "customer_id=eq.41". Bỏ trống = toàn bảng. */
    filter?: string;
    /** Loại event muốn nghe. Mặc định "*" (INSERT/UPDATE/DELETE). */
    event?: "INSERT" | "UPDATE" | "DELETE" | "*";
    /** Gọi khi có thay đổi. Theo hướng refetch nên thường bỏ qua payload. */
    onEvent: (payload: any) => void;
}

/**
 * Lắng nghe thay đổi trên bảng channel_messages.
 * @returns hàm cleanup để hủy subscription (no-op nếu realtime chưa bật).
 */
export function subscribeChannelMessages({
    channelName,
    filter,
    event = "*",
    onEvent,
}: SubscribeOptions): () => void {
    const c = getClient();
    if (!c) return () => { /* realtime chưa bật */ };

    const channel: RealtimeChannel = c
        .channel(channelName)
        .on(
            "postgres_changes" as any,
            {
                event: event as any,
                schema: "public",
                table: CHANNEL_MESSAGES_TABLE,
                ...(filter ? { filter } : {}),
            },
            (payload: any) => onEvent(payload)
        )
        .subscribe((status) => {
            if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
                // Không chặn UI: phòng chat vẫn chạy nhờ polling dự phòng.
                console.warn(`[realtime] ${channelName} status: ${status}`);
            }
        });

    return () => {
        c.removeChannel(channel);
    };
}

export interface SubscribeNotificationsOptions {
    /** Tên kênh (mặc định theo gợi ý của API). */
    channelName?: string;
    /** Filter postgres_changes, vd "recipient_id=eq.7". Bỏ trống = nhận INSERT toàn bảng. */
    filter?: string;
    /** Gọi khi có thông báo mới (payload.new). */
    onInsert: (row: any) => void;
    /** Cập nhật trạng thái kết nối realtime (SUBSCRIBED / CHANNEL_ERROR / ...). */
    onStatus?: (status: string) => void;
}

/**
 * Lắng nghe INSERT trên bảng notifications (thông báo mới).
 * @returns hàm cleanup hủy subscription (no-op nếu realtime chưa bật).
 */
export function subscribeNotifications({
    channelName = "public:notifications",
    filter,
    onInsert,
    onStatus,
}: SubscribeNotificationsOptions): () => void {
    const c = getClient();
    if (!c) {
        onStatus?.("DISABLED");
        return () => { /* realtime chưa bật */ };
    }

    onStatus?.("CONNECTING");
    const channel: RealtimeChannel = c
        .channel(channelName)
        .on(
            "postgres_changes" as any,
            {
                event: "INSERT",
                schema: "public",
                table: NOTIFICATIONS_TABLE,
                ...(filter ? { filter } : {}),
            },
            (payload: any) => onInsert(payload.new)
        )
        .subscribe((status) => {
            onStatus?.(status);
            if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
                console.warn(`[realtime] ${channelName} status: ${status}`);
            }
        });

    return () => {
        c.removeChannel(channel);
    };
}
